import type { AxiosInstance } from 'axios';

// Firma del predicado: recibe `err.response.data` (o undefined si no hay
// response) y decide si el mensaje que viene ahi es user-friendly. Cuando
// devuelve false, el interceptor vacia `data` y `err.message` (undefined,
// NO string vacio — `??` en la cadena de fallbacks los trata distinto), y
// el consumer termina cayendo al fallback generico de useSubmit.
export type IsBusinessError = (data: any) => boolean;

// Aplica un interceptor de respuesta que filtra los errores que NO son
// presentables al usuario (proxy timeouts, network errors, 500 sin marca
// del back, HTML de gateways, etc.). Cada instance de axios recibe su
// propio predicado porque cada back arma el wrapper de excepcion distinto.
export function attachErrorInterceptor(
  instance: AxiosInstance,
  isBusinessError: IsBusinessError,
): void {
  instance.interceptors.response.use(
    (r) => r,
    (err) => {
      const data = err?.response?.data;
      if (!isBusinessError(data)) {
        // Preservamos el payload original en `originalData` — algunos flujos
        // (ej: chequear si un turno ya fue reprogramado) leen el
        // `exceptionMessage` programaticamente aunque el back no lo marque
        // solucionable. La UI, en cambio, ve `data: undefined` y cae al
        // fallback via useSubmit.
        if (err?.response) {
          (err.response as any).originalData = data;
          err.response.data = undefined;
        }
        (err as any).message = undefined;
      } else if (
        data &&
        typeof data === 'object' &&
        typeof data.exceptionMessage === 'string'
      ) {
        // Normalizamos el exceptionMessage que viene JSON-encoded (business
        // errors wrappeados como System.Exception → `"\"<msg>\""`). Lo
        // reemplazamos en el payload asi los consumers que leen directo
        // `err.response.data.exceptionMessage` (ej: useSubmit) muestran el
        // mensaje limpio sin comillas.
        const clean = data.exceptionMessage.replace(/^"+|"+$/g, '').trim();
        if (clean && clean !== data.exceptionMessage) {
          data.exceptionMessage = clean;
        }
      }
      return Promise.reject(err);
    },
  );
}
